import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const GalleryManagement = () => {
  const [gallery, setGallery] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    image_link: '',
    description: ''
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      setError('');
      console.log('Fetching gallery from:', API_ENDPOINTS.ADMIN.GALLERY);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.GALLERY, {
        timeout: 8000,
      });
      
      console.log('Gallery data received:', response.data);
      setGallery(response.data);
      setSelectedItems([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      handleApiError(error, 'fetch gallery items');
    } finally {
      setLoading(false);
    }
  };

  const handleApiError = (error, operation) => {
    if (error.code === 'ECONNABORTED') {
      setError(`Request timeout while trying to ${operation}. Please check your connection.`);
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Failed to ${operation}.`);
    } else if (error.request) {
      setError(`Network error. Please check if the server is running.`);
    } else {
      setError(`Unable to ${operation}. Please try again later.`);
    }
  };

  const showSuccess = (text) => {
    setSuccess(text);
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setError('');
      const response = await axios.post(API_ENDPOINTS.ADMIN.GALLERY, formData, {
        timeout: 8000,
      });
      
      console.log('Add gallery response:', response.data);
      await fetchGallery();
      setShowForm(false);
      setFormData({ image_link: '', description: '' });
      showSuccess('Gallery item added successfully!');
    } catch (error) {
      console.error('Error creating gallery item:', error);
      if (error.response && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        handleApiError(error, 'add gallery item');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      setLoading(true);
      try {
        setError('');
        await axios.delete(`${API_ENDPOINTS.ADMIN.GALLERY}/${id}`, {
          timeout: 8000,
        });
        
        await fetchGallery();
        showSuccess('Gallery item deleted successfully!');
      } catch (error) {
        console.error('Error deleting gallery item:', error);
        handleApiError(error, 'delete gallery item');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one gallery item to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} gallery item(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      setError('');
      await axios.post(`${API_ENDPOINTS.ADMIN.GALLERY}/bulk_delete`, {
        gallery_ids: selectedItems
      }, {
        timeout: 8000,
      });
      
      await fetchGallery();
      showSuccess(`Successfully deleted ${selectedItems.length} gallery item(s)`);
    } catch (error) {
      console.error('Error bulk deleting gallery items:', error);
      handleApiError(error, 'delete gallery items');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(gallery.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isSelected = (itemId) => selectedItems.includes(itemId);

  // Test image URLs for common issues
  const getSafeImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/300x200?text=No+Image';
    
    // Fix common URL issues
    let safeUrl = url.trim();
    
    // Ensure it's a valid URL
    try {
      new URL(safeUrl);
      return safeUrl;
    } catch (e) {
      return 'https://via.placeholder.com/300x200?text=Invalid+URL';
    }
  };

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gallery Management</h2>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin me-2"></i>Loading...
              </>
            ) : (
              <>
                <i className="fas fa-plus me-2"></i>Add Image
              </>
            )}
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedItems.length > 0 && (
          <div className="bulk-actions-toolbar mb-3 p-3 bg-primary text-white rounded d-flex justify-content-between align-items-center">
            <span>
              <strong>{selectedItems.length}</strong> gallery item(s) selected
            </span>
            <div>
              <button 
                className="btn btn-danger btn-sm me-2"
                onClick={handleBulkDelete}
                disabled={loading}
                title="Delete Selected"
              >
                {loading ? (
                  <i className="fas fa-spinner fa-spin me-1"></i>
                ) : (
                  <i className="fas fa-trash me-1"></i>
                )}
                Delete Selected
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedItems([])}
                disabled={loading}
                title="Clear Selection"
              >
                <i className="fas fa-times me-1"></i>Clear
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <div className="card form-container mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0">Add New Gallery Item</h4>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Image URL *</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                    value={formData.image_link}
                    onChange={(e) => setFormData({ ...formData, image_link: e.target.value })}
                    required
                    disabled={loading}
                  />
                  <div className="form-text">
                    Enter the direct link to the image. Must be a publicly accessible URL.
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Describe this image..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    disabled={loading}
                  ></textarea>
                </div>
                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>Adding...
                      </>
                    ) : (
                      'Save Image'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Select All Checkbox */}
        {gallery.length > 0 && (
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                id="selectAllGallery"
                disabled={loading}
              />
              <label className="form-check-label fw-bold" htmlFor="selectAllGallery">
                Select All ({gallery.length} items)
              </label>
            </div>
          </div>
        )}

        {loading && gallery.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fa-2x text-primary mb-3"></i>
            <p>Loading gallery items...</p>
          </div>
        ) : gallery.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-images fa-3x text-muted mb-3"></i>
            <p>No gallery items found. Add some images to get started.</p>
          </div>
        ) : (
          <div className="row">
            {gallery.map(item => (
              <div key={item.id} className="col-md-4 mb-4">
                <div className={`card h-100 ${isSelected(item.id) ? 'border-primary border-2' : ''}`}>
                  <div className="card-header p-2 bg-light d-flex justify-content-between align-items-center">
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isSelected(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        id={`gallery-${item.id}`}
                        disabled={loading}
                      />
                      <label className="form-check-label small" htmlFor={`gallery-${item.id}`}>
                        Select
                      </label>
                    </div>
                    <small className="text-muted">ID: {item.id}</small>
                  </div>
                  <div className="position-relative">
                    <img 
                      src={getSafeImageUrl(item.image_link)} 
                      className="card-img-top" 
                      alt={item.description || 'Gallery image'}
                      style={{ 
                        height: '200px', 
                        objectFit: 'cover',
                        backgroundColor: '#f8f9fa'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                        e.target.alt = 'Image not found';
                      }}
                    />
                    {loading && (
                      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light bg-opacity-75">
                        <i className="fas fa-spinner fa-spin text-primary fa-2x"></i>
                      </div>
                    )}
                  </div>
                  <div className="card-body d-flex flex-column">
                    <p className="card-text flex-grow-1">
                      {item.description || <span className="text-muted fst-italic">No description</span>}
                    </p>
                    <div className="mt-auto">
                      <div className="d-grid">
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(item.id)}
                          disabled={loading}
                        >
                          {loading ? (
                            <i className="fas fa-spinner fa-spin me-1"></i>
                          ) : (
                            <i className="fas fa-trash me-1"></i>
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .bulk-actions-toolbar {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
        .card {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
      `}</style>
    </AdminLayout>
  );
};

export default GalleryManagement;